import type { Quadrilateral, QuadrilateralMap } from './types';

import { pluscodeMap } from '@/core/service/googleMaps/utils/pluscodeMap';

import OpenLocationCode from './openlocationcode';

/* Esse arquivo por enquanto não possui testes unitários,
   ja que a prioridade mudou para a implementação da listagem dos imoveis

   TODO: Implementar testes unitários
*/

/**
 * Validates a pluscode string.
 *
 * @param {string} pluscode - The pluscode to validate.
 * @returns {boolean} Returns true if the pluscode is valid, false otherwise.
 */
function isValidPluscode(pluscode: string): boolean {
	const regex = /^[A-Z0-9]{8}\+$/;
	return regex.test(pluscode);
}
/**
 * Retrieves the pluscode group (first 6 characters) from a valid pluscode.
 *
 * @param {string} pluscode - The pluscode to process.
 * @returns {string} The pluscode group.
 * @throws {Error} If the pluscode is invalid.
 */
function getPluscodeGroup(pluscode: string): string {
	const isValid = isValidPluscode(pluscode);

	if (!isValid) {
		throw new Error('Invalid pluscode');
	}

	return pluscode.slice(0, -3);
}
/**
 * Retrieves the key portion (last 2 characters) of a valid pluscode.
 *
 * @param {string} pluscode - The pluscode to process.
 * @returns {string} The pluscode key.
 * @throws {Error} If the pluscode is invalid.
 */
function getPluscodeKey(pluscode: string): string {
	const isValid = isValidPluscode(pluscode);

	if (!isValid) {
		throw new Error('Invalid pluscode');
	}

	return pluscode.slice(-3, -1);
}
/**
 * Encodes a group key by concatenating the pluscode group and mapped group keys.
 *
 * @param {string} pluscode - The pluscode to encode.
 * @returns {string|null} The encoded group key or null if no group key is found.
 */
function encodeGroupKey(pluscode: string): string | null {
	const pluscodeKey = getPluscodeKey(pluscode);
	const pluscodeGroup = getPluscodeGroup(pluscode);

	const groupKeys = pluscodeMap.find(group => group.includes(pluscodeKey));

	if (!groupKeys) {
		return null;
	}

	return pluscodeGroup + groupKeys.join('');
}
/**
 * Decodes a group key into a list of pluscodes.
 *
 * @param {string} group - The group key to decode.
 * @returns {string[]} The decoded pluscodes.
 * @throws {Error} If the group key is invalid.
 */
function decodeGroupKey(group: string): string[] {
	const pluscodeGroup = group.slice(0, 6);
	const pluscodesKey = group.slice(6).match(/.{1,2}/g);

	if (!pluscodesKey) {
		throw new Error('Invalid group key');
	}

	return pluscodesKey?.map(key => pluscodeGroup + key + '+');
}
/**
 * Gets the center coordinates of a group key.
 *
 * @param {string} groupKey - The group key to process.
 * @returns {google.maps.LatLng} The center coordinates of the group.
 * @throws {Error} If the group key is invalid.
 */
export function getCenterCoordinatesOfGroup(
	groupKey: string
): google.maps.LatLng {
	const pluscodes = decodeGroupKey(groupKey);
	const centerPluscode = pluscodes[6];

	if (!centerPluscode) {
		throw new Error('Invalid group key');
	}

	const centerPluscodeArea = OpenLocationCode.decode(centerPluscode) as any;

	return new google.maps.LatLng(
		centerPluscodeArea.latitudeHi,
		centerPluscodeArea.longitudeLo
	);
}
/**
 * A record of indexes that map to the coordinates of the edges of a quadrilateral.
 * The keys are the position indices, and the values are tuples containing strings
 * representing the coordinate names ('latitude' and 'longitude').
 */
const EDGES_INDEXES_BY_POSITION: Record<number, [string, string]> = {
	0: ['latitudeLo', 'longitudeLo'],
	3: ['latitudeLo', 'longitudeHi'],
	12: ['latitudeHi', 'longitudeLo'],
	15: ['latitudeHi', 'longitudeHi']
};
/**
 * Gets the path coordinates for the corners of a group key.
 *
 * @param {string} groupKey - The group key to process.
 * @returns {google.maps.LatLng[]} The coordinates of the quadrilateral formed by the group.
 * @throws {Error} If the group key is invalid or latitude/longitude values are incorrect.
 */
export function getPathsCoordinatesOfGroup(
	groupKey: string
): google.maps.LatLng[] {
	const pluscodes = decodeGroupKey(groupKey);

	const positionsOrder = [0, 3, 15, 12];

	const coordinates = positionsOrder.reduce<google.maps.LatLng[]>(
		(coordinates, positionIndex) => {
			const pluscode = pluscodes[positionIndex];
			const position = EDGES_INDEXES_BY_POSITION[positionIndex];

			if (!position || !pluscode) {
				return coordinates;
			}

			const area = OpenLocationCode.decode(pluscode) as any; // TODO: corrigir tipagem
			const lat = area[position[0]];
			const lng = area[position[1]];

			if (typeof lat !== 'number' || typeof lng !== 'number') {
				throw new Error('Invalid latitude or longitude values');
			}

			const coordinatesByPosition = new google.maps.LatLng(lat, lng);

			return [...coordinates, coordinatesByPosition];
		},
		[]
	);

	return coordinates;
}
/**
 * Gets all pluscode quadrilaterals within the specified bounds.
 *
 * @param {google.maps.LatLngBounds} bounds - The bounds to search within.
 * @param {number} [stepSize=0.001] - The step size for iterating through the bounds.
 * @returns {string[]} A unique list of encoded group keys within the bounds.
 */
export function getGroupKeysInBounds(
	bounds: google.maps.LatLngBounds,
	stepSize: number = 0.001
): string[] {
	try {
		const { north, south, east, west } = bounds?.toJSON?.() || {};

		const plusCodes: string[] = [];

		for (let lat = south; lat <= north; lat += stepSize) {
			for (let lng = west; lng <= east; lng += stepSize) {
				const plusCode = OpenLocationCode.encode(lat, lng, 8);
				const groupKey = encodeGroupKey(plusCode);

				if (!groupKey) continue;

				plusCodes.push(groupKey);
			}
		}

		return [...new Set<string>(plusCodes)];
	} catch (e) {
		return [];
	}
}
/**
 * Creates a quadrilateral object based on the group key and count.
 *
 * @param {string} groupKey - The group key to use.
 * @param {number} count - The count associated with the quadrilateral.
 * @returns {Quadrilateral} The quadrilateral object.
 */
export function getQuadrilateral(
	groupKey: string,
	count: number
): Quadrilateral {
	const centerCoordinates = getCenterCoordinatesOfGroup(groupKey);
	const pathsCoordinates = getPathsCoordinatesOfGroup(groupKey);

	return {
		centerCoordinates,
		pathsCoordinates,
		count,
		groupKey
	};
}
/**
 * Maps a dictionary of group keys to quadrilaterals.
 *
 * @param {Record<string, number>} dict - The dictionary of group keys and counts.
 * @returns {QuadrilateralMap} A map of group keys to quadrilaterals.
 */
export function getQuadrilaterals(
	dict: Record<string, number>
): QuadrilateralMap {
	const quadrilaterals = new Map<string, Quadrilateral>();

	for (const groupKey of Object.keys(dict)) {
		const quadrilateral = getQuadrilateral(groupKey, dict[groupKey] || 0);

		quadrilaterals.set(groupKey, quadrilateral);
	}

	return quadrilaterals;
}
/**
 * Generates a map of quadrilaterals based on a list of group keys.
 *
 * @param {string[]} groupKeys - An array of group keys that represent different quadrilaterals.
 * @returns {QuadrilateralMap} - A map where each key is a groupKey and the value is a Quadrilateral object.
 */
export function getQuadrilateralsFromGroupKeys(
	groupKeys: string[]
): QuadrilateralMap {
	const quadrilaterals = new Map<string, Quadrilateral>();

	for (const groupKey of groupKeys) {
		const quadrilateral = getQuadrilateral(groupKey, 0);

		quadrilaterals.set(groupKey, quadrilateral);
	}

	return quadrilaterals;
}
/**
 * Filters a list of group keys to remove keys already present in a quadrilateral map.
 *
 * @param {string[]} groupKeyList - The list of group keys to filter.
 * @param {QuadrilateralMap} map - The map of existing quadrilaterals.
 * @returns {string[]} The filtered list of group keys.
 */
export function filterGroupKeyList(
	groupKeyList: string[],
	map: QuadrilateralMap
): string[] {
	return groupKeyList.filter(groupKey => !map.has(groupKey));
}
/**
 * Merges a dictionary of group keys and counts with an existing quadrilateral map.
 *
 * @param {QuadrilateralMap} map - The existing quadrilateral map.
 * @param {Record<string, number>} dict - The dictionary of group keys and counts.
 * @returns {QuadrilateralMap} The updated quadrilateral map.
 */
export function mergeGroupKeyLists(
	map: QuadrilateralMap,
	dict: Record<string, number>
): QuadrilateralMap {
	const groupKeyList = Object.keys(dict);

	for (const groupKey of groupKeyList) {
		const count = dict[groupKey] || 0;

		if (map.has(groupKey)) {
			const quadrilateral = map.get(groupKey);

			if (!quadrilateral) continue;

			map.set(groupKey, {
				...quadrilateral,
				count: count
			});
		} else {
			const quadrilateral = getQuadrilateral(groupKey, count);
			map.set(groupKey, quadrilateral);
		}
	}

	return map;
}
/**
 * Updates the count property of each quadrilateral in a `QuadrilateralMap` based on the provided dictionary.
 * If a group key exists in the dictionary, its corresponding quadrilateral's count is updated.
 * If a group key does not exist in the dictionary, a new quadrilateral with a count of 0 is created and added to the map.
 *
 * @param {QuadrilateralMap} map - The map containing quadrilateral data to be updated.
 * @param {Record<string, number>} dict - An object that provides the count values for each group key.
 * @returns {QuadrilateralMap} - The updated `QuadrilateralMap` with the new counts.
 */
export function updateGroupsCount(
	map: QuadrilateralMap,
	dict: Record<string, number>
): QuadrilateralMap {
	const groupKeyList = map.keys();
	const groupKeys = Object.keys(dict);

	for (const groupKey of groupKeyList) {
		const newCount = dict[groupKey] || 0;

		if (groupKeys.includes(groupKey)) {
			const quadrilateral = map.get(groupKey);

			if (!quadrilateral) continue;

			map.set(groupKey, {
				...quadrilateral,
				count: newCount
			});
		} else {
			const quadrilateral = getQuadrilateral(groupKey, 0);
			map.set(groupKey, quadrilateral);
		}
	}

	return map;
}

/**
 * Gets a list of unmapped group keys from a list of group keys.
 *
 * @param {QuadrilateralMap} map - The quadrilateral map.
 * @param {string[]} groupKeysList - The list of group keys to check.
 * @returns {string[]} A list of unmapped group keys.
 */
export function getUnmappedGroupKeys(
	map: QuadrilateralMap,
	groupKeysList: string[]
): string[] {
	return groupKeysList.filter(groupKey => !map.has(groupKey));
}
/**
 * Calculates the geographic bounds (LatLngBounds) for a screen based on the center point and zoom level.
 *
 * @param {google.maps.LatLngLiteral} center - The center latitude and longitude of the screen.
 * @param {number} zoomLevel - The zoom level to calculate the bounds.
 * @returns {google.maps.LatLngBounds} - The calculated geographic bounds based on the screen size and zoom level.
 *
 * @example
 * const center = { lat: 37.7749, lng: -122.4194 };
 * const zoom = 12;
 * const bounds = calculateBoundsFromScreen(center, zoom);
 */
export function calculateBoundsFromScreen(
	center: google.maps.LatLngLiteral,
	zoomLevel: number
): google.maps.LatLngBounds {
	const { lat, lng } = center;
	const screenWidth = window.innerWidth;
	const screenHeight = window.innerHeight;

	const metersPerPixelAtZoom1 = 90000;

	const metersPerPixel = metersPerPixelAtZoom1 / Math.pow(2, zoomLevel);

	const viewWidthMeters = screenWidth * metersPerPixel;
	const viewHeightMeters = screenHeight * metersPerPixel;

	const scaleFactor = 111000;
	const latDelta = viewHeightMeters / scaleFactor;
	const lngDelta =
		viewWidthMeters / (scaleFactor * Math.cos(lat * (Math.PI / 180)));

	const bounds = new google.maps.LatLngBounds(
		new google.maps.LatLng(lat - latDelta / 2, lng - lngDelta / 2),
		new google.maps.LatLng(lat + latDelta / 2, lng + lngDelta / 2)
	);

	return bounds;
}
